import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Camera as CameraIcon, Plus, Trash2, Loader2, Search, Building2, User } from "lucide-react";

/**
 * Super-admin panel to manage `camera_access` phone list for any LD.
 * Also lets the super-admin set/update a member's `telefon` (needed for camera-access match).
 */
export const SuperAdminCameras = () => {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [lds, setLds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedLd, setSelectedLd] = useState(null);
  const [accessList, setAccessList] = useState([]);
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState("");

  // Load LDs on mount
  useEffect(() => {
    axios.get(`${API}/superadmin/lovske-druzine`, { headers: authHeaders })
      .then((r) => setLds((r.data || []).filter((l) => l.aktivna)))
      .catch(() => toast.error("Napaka pri branju LD-jev"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const filteredLds = lds.filter((l) =>
    !search || (l.naziv || "").toLowerCase().includes(search.toLowerCase()) || (l.koda || "").toLowerCase().includes(search.toLowerCase())
  );

  const pickLd = async (ld) => {
    setSelectedLd(ld);
    setAccessList([]);
    setMembers([]);
    setEditingUser(null);
    try {
      const r = await axios.get(`${API}/cameras/access-list?ld_id=${encodeURIComponent(ld.id)}`, { headers: authHeaders });
      setAccessList(r.data.camera_access || []);
    } catch (e) {
      toast.error("Napaka pri branju seznama dostopa");
    }
    // load members for that LD
    setLoadingMembers(true);
    try {
      const r = await axios.get(`${API}/superadmin/lovske-druzine/${ld.id}/members`, { headers: authHeaders });
      setMembers(r.data || []);
    } catch (e) {
      // fallback if endpoint doesn't exist
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const saveAccessList = async (nextList) => {
    if (!selectedLd) return;
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/cameras/access-list`,
        { camera_access: nextList, ld_id: selectedLd.id },
        { headers: authHeaders }
      );
      setAccessList(r.data.camera_access);
      toast.success("Shranjeno");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    } finally {
      setSaving(false);
    }
  };

  const addPhone = () => {
    const t = newPhone.trim();
    if (!t) return;
    saveAccessList([...accessList, t]);
    setNewPhone("");
  };

  const removePhone = (idx) => saveAccessList(accessList.filter((_, i) => i !== idx));

  const startEditPhone = (m) => {
    setEditingUser(m.id);
    setPhoneDraft(m.telefon || "");
  };

  const saveUserPhone = async (m) => {
    try {
      await axios.put(
        `${API}/users/${m.id}`,
        { telefon: phoneDraft.trim() || null },
        { headers: authHeaders }
      );
      setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, telefon: phoneDraft.trim() } : x)));
      setEditingUser(null);
      toast.success("Telefon posodobljen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    }
  };

  const addMemberPhoneToAccess = (m) => {
    if (!m.telefon) {
      toast.error("Član nima vpisane telefonske številke");
      return;
    }
    if (accessList.some((p) => p.replace(/\D/g, "").slice(-8) === m.telefon.replace(/\D/g, "").slice(-8))) {
      toast.info("Ta številka je že na seznamu");
      return;
    }
    saveAccessList([...accessList, m.telefon]);
  };

  return (
    <div className="space-y-4" data-testid="sa-cameras-panel">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CameraIcon className="h-5 w-5 text-primary" />
            Kamere — dostop po LD-jih
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Izberi LD in določi, katere telefonske številke lahko dostopajo do SpyPoint kamer.
            Ujemanje po zadnjih 8 številkah (`041 350 782` = `+38641350782`).
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Išči LD po nazivu ali kodi…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="sa-cam-search"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {filteredLds.map((ld) => (
                <button
                  key={ld.id}
                  onClick={() => pickLd(ld)}
                  className={`text-left border rounded-lg p-3 hover:border-primary transition ${selectedLd?.id === ld.id ? "border-primary bg-primary/5" : "border-slate-200"}`}
                  data-testid={`sa-cam-ld-${ld.koda}`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm truncate">{ld.naziv}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {(ld.camera_access?.length || 0)} št. na seznamu
                  </div>
                </button>
              ))}
              {filteredLds.length === 0 && (
                <p className="text-sm text-muted-foreground col-span-full text-center py-4">Ni zadetkov</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedLd && (
        <>
          <Card className="border-2 border-amber-300 bg-amber-50/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Seznam dostopa · {selectedLd.naziv}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="npr. 041 350 782"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addPhone()}
                  data-testid="sa-cam-new-phone"
                />
                <Button onClick={addPhone} disabled={saving || !newPhone.trim()} data-testid="sa-cam-add-phone">
                  <Plus className="h-4 w-4 mr-1" /> Dodaj
                </Button>
              </div>
              {accessList.length === 0 ? (
                <p className="text-sm text-slate-500">Seznam je prazen.</p>
              ) : (
                <ul className="divide-y divide-slate-200 bg-white rounded-lg">
                  {accessList.map((p, idx) => (
                    <li key={idx} className="flex items-center justify-between px-3 py-2" data-testid={`sa-cam-access-${idx}`}>
                      <span className="text-sm font-mono text-slate-700">{p}</span>
                      <Button size="sm" variant="ghost" onClick={() => removePhone(idx)} disabled={saving}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" /> Člani LD in njihove telefonske številke
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Uredi telefonsko številko posameznega člana. Klikni „+&quot; da hitro dodaš njegovo številko na seznam dostopa.
              </p>
            </CardHeader>
            <CardContent>
              {loadingMembers ? (
                <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>
              ) : members.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ni članov (ali endpoint /superadmin/lovske-druzine/[id]/members ni na voljo v tem buildu).</p>
              ) : (
                <ul className="divide-y divide-slate-200">
                  {members.map((m) => (
                    <li key={m.id} className="flex items-center gap-2 py-2 text-sm" data-testid={`sa-cam-member-${m.id}`}>
                      <span className="flex-1 truncate">
                        <span className="font-medium">{m.ime} {m.priimek}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{m.vloga}</span>
                      </span>
                      {editingUser === m.id ? (
                        <>
                          <Input
                            className="w-40"
                            value={phoneDraft}
                            onChange={(e) => setPhoneDraft(e.target.value)}
                            placeholder="041 350 782"
                            autoFocus
                          />
                          <Button size="sm" onClick={() => saveUserPhone(m)}>Shrani</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingUser(null)}>×</Button>
                        </>
                      ) : (
                        <>
                          <span className="w-40 font-mono text-xs text-slate-600 truncate">{m.telefon || "—"}</span>
                          <Button size="sm" variant="outline" onClick={() => startEditPhone(m)}>Uredi</Button>
                          {m.telefon && (
                            <Button size="sm" variant="secondary" onClick={() => addMemberPhoneToAccess(m)} title="Dodaj številko na seznam dostopa">
                              <Plus className="h-3 w-3" />
                            </Button>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
